import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import PartnerSummary from './PartnerSummary';
import PartnerComponent from './PartnerComponent';
import { get } from '@totalsoft/change-tracking-react';
import { CollapseCard } from '@totalsoft/rocket-ui';
import FakeTextWrapper from 'components/common/FakeTextWrapper';

const PartnerCard = ({
  partnerLens,
  insuranceTypeCode,
  cascoInsuranceTypeCode,
  partnerTypes,
  disabledStatus,
  title,
  icon,
  activityTypes,
  usageTypes,
  counties,
  cities,
  citizenships,
  nationalities,
  defaultCountryCode,
  personDocumentTypes,
  isPF,
  hasRightToSearch,
  validation,
  CustomContent,
  loadingStatus,
  onSave,
  onUniqueIdBlur,
  onContactPersonUniqueIdBlur,
  onUniqueIdChanged,
  onContactPersonUniqueIdChanged,
  onPartnerTypeChange,
  onDistrictChange,
  onOpenSearchPartnersModal,
  ocrContext
}) => {
  const { t } = useTranslation();

  const composeControlId = useCallback(
    controlKey => {
      return `${title}.${controlKey}`;
    },
    [title]
  );

  const getUniqueIdLabel = useCallback(() => {
    return isPF(partnerLens.partnerTypeId |> get) ? t('Partner.UniqueID.CNP') : t('Partner.UniqueID.CUI');
  }, [partnerLens, t, isPF]);

  return (
    <CollapseCard
      id='partnerCard_CollapseCard'
      icon={icon}
      title={t(title)}
      subheader={
        <PartnerSummary
          title={title}
          partner={partnerLens |> get}
          loading={loadingStatus.loading}
          isPF={isPF}
          SummaryExtension={CustomContent?.SummaryExtension}
        />
      }
      hideSubheaderOnExpand
      toggleOnHeaderClick
    >
      {loadingStatus.loading ? (
        <FakeTextWrapper />
      ) : (
        <PartnerComponent
          partnerLens={partnerLens}
          insuranceTypeCode={insuranceTypeCode}
          cascoInsuranceTypeCode={cascoInsuranceTypeCode}
          partnerTypes={partnerTypes}
          disabledStatus={disabledStatus}
          activityTypes={activityTypes}
          usageTypes={usageTypes}
          counties={counties}
          cities={cities}
          citizenships={citizenships}
          nationalities={nationalities}
          defaultCountryCode={defaultCountryCode}
          personDocumentTypes={personDocumentTypes}
          isPF={isPF}
          hasRightToSearch={hasRightToSearch}
          validation={validation}
          CustomContent={CustomContent}
          loadingStatus={loadingStatus}
          composeControlId={composeControlId}
          getUniqueIdLabel={getUniqueIdLabel}
          onSave={onSave}
          onUniqueIdBlur={onUniqueIdBlur}
          onContactPersonUniqueIdBlur={onContactPersonUniqueIdBlur}
          onUniqueIdChanged={onUniqueIdChanged}
          onContactPersonUniqueIdChanged={onContactPersonUniqueIdChanged}
          onPartnerTypeChange={onPartnerTypeChange}
          onDistrictChange={onDistrictChange}
          onOpenSearchPartnersModal={onOpenSearchPartnersModal}
          ocrContext={ocrContext}
        />
      )}
    </CollapseCard>
  );
};

PartnerCard.propTypes = {
  partnerLens: PropTypes.object.isRequired,
  insuranceTypeCode: PropTypes.string,
  cascoInsuranceTypeCode: PropTypes.string,
  partnerTypes: PropTypes.array,
  disabledStatus: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired,
  icon: PropTypes.object.isRequired,
  activityTypes: PropTypes.array,
  usageTypes: PropTypes.array,
  counties: PropTypes.array,
  cities: PropTypes.array,
  citizenships: PropTypes.array,
  nationalities: PropTypes.array,
  defaultCountryCode: PropTypes.string,
  personDocumentTypes: PropTypes.array,
  isPF: PropTypes.func.isRequired,
  hasRightToSearch: PropTypes.bool,
  validation: PropTypes.object,
  CustomContent: PropTypes.object,
  loadingStatus: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired,
  onUniqueIdBlur: PropTypes.func,
  onContactPersonUniqueIdBlur: PropTypes.func,
  onUniqueIdChanged: PropTypes.func,
  onContactPersonUniqueIdChanged: PropTypes.func,
  onPartnerTypeChange: PropTypes.func,
  onDistrictChange: PropTypes.func,
  onOpenSearchPartnersModal: PropTypes.func,
  onCopyPartner: PropTypes.func,
  ocrContext: PropTypes.object
};

export default PartnerCard;
